import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { AXIOS_API_INSTANCE, SERVICE_ORDER_SERVICE_COLOR_MAP, SERVICE_ORDER_SERVICE_FRIENDLY_NAMES } from "../../../Constants"
import { send_authorized_request } from "../../../Constants/cognito"
import { useSnackbar } from 'notistack';
import { Alert, Autocomplete, Box, Chip, CircularProgress, IconButton, InputAdornment } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { LoadingBackdropIndeterminate } from "../../LoadingBackdrop"
import dayjs from "dayjs"
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { build_users_list, findUser, useUsers } from "../../ReactQuery/QueryUsers"
import UserAvatar from "../../UserAvatar"
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { useTrackedLocation, useTrackedUser } from "../../UserState"
import { findLocation, useLocations } from "../../ReactQuery/QueryLocations"
import { serviceOrderChip } from "../../../Utils/service"
import { formatCustomerName } from "../../../Utils"

export default function ServiceWorkHistoryModal({
    serviceOrderWorkHistory,
    open,
    closeModal,
    creatingNewWorkHistory
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [my_user,] = useTrackedUser();
    const { data: users, isLoadingUsers } = useUsers();
    const { data: locations } = useLocations();
    const [selectedLocation,] = useTrackedLocation();
    // const [notes, setNotes] = React.useState("")
    const [serviceOrder, setServiceOrder] = React.useState({})
    const [serviceOrderLoading, setServiceOrderLoading] = React.useState(false)
    const [serviceData, setServiceData] = React.useState({})
    const [serviceOrderSearch, setServiceOrderSearch] = React.useState('')
    const [selectedTechnician, setSelectedTechnician] = React.useState({})
    const [mySelectedLocation, setMySelectedLocation] = React.useState(selectedLocation)
    const [overheadNote, setOverheadNote] = React.useState("")
    const [diagnosticNote, setDiagnosticNote] = React.useState("")
    const [diagnosticMinutes, setDiagnosticMinutes] = React.useState(0)
    const [startTime, setStartTime] = React.useState(dayjs.utc())
    const [endTime, setEndTime] = React.useState(dayjs.utc())
    const [saving, setSaving] = React.useState(false)
    const totalMinutes = dayjs.utc(endTime).diff(dayjs.utc(startTime), 'minutes')
    const known_user = findUser(users, serviceOrderWorkHistory?.user_id)

    const handleChange = (id, prop) => (event) => {
        if (['recorded_minutes'].includes(prop)) {
            serviceData[id][prop] = Math.max(parseInt(event.target.value || 0), 0)
        } else {
            serviceData[id][prop] = event.target.value
        }
        setServiceData({ ...serviceData })
    }

    const save = (event) => {
        setSaving(true)
        const data = {
            service_data: serviceData,
            overhead_note: overheadNote.trim(),
            diagnostic_minutes: diagnosticMinutes,
            diagnostic_note: diagnosticNote.trim(),
            start_time: startTime,
            end_time: endTime
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(`serviceorderworkhistory/${serviceOrderWorkHistory.id}`, data).then((response) => {
                setSaving(false)
                enqueueSnackbar('Successfully edited work history record', { variant: 'success' })
                closeModal(true)
            }).catch((error) => {
                setSaving(false)
                enqueueSnackbar(`Failed to edit work history <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    const create = (event) => {
        setSaving(true)
        const data = {
            service_order_id: serviceOrder.id,
            user_id: selectedTechnician.id,
            location_id: mySelectedLocation.id,
            service_data: serviceData,
            overhead_note: overheadNote.trim(),
            diagnostic_minutes: diagnosticMinutes,
            diagnostic_note: diagnosticNote.trim(),
            start_time: startTime,
            end_time: endTime
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.put(`serviceorderworkhistory`, data).then((response) => {
                setSaving(false)
                enqueueSnackbar('Successfully created work history record', { variant: 'success' })
                closeModal(true)
            }).catch((error) => {
                setSaving(false)
                enqueueSnackbar(`Failed to create work history <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    const getServiceOrder = (id) => {
        setServiceOrderLoading(true)
        send_authorized_request(() => AXIOS_API_INSTANCE.get(`serviceorders/${id}`).then((new_serviceOrder) => {
            setServiceOrder(new_serviceOrder.data.data)
            let new_serviceData = {}
            new_serviceOrder.data.data.service_order_services.forEach((service) => {
                const existing_record = (serviceOrderWorkHistory?.service_order_work_history_entries ?? []).find((entry) => entry.service_order_service_id === service.id)
                new_serviceData[service.id] = {
                    note: existing_record?.note ?? "",
                    recorded_minutes: existing_record?.recorded_minutes ?? 0
                }
            })
            setServiceData(new_serviceData)
            setServiceOrderLoading(false)
        }).catch((error) => {
            console.log(error);
            setServiceOrder({})
            setServiceOrderLoading(false)
            enqueueSnackbar(`Failed to load service order: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
        }))
    }

    const ready = () => {
        if (creatingNewWorkHistory) {
            return serviceOrder.id && selectedTechnician.id && mySelectedLocation.id && startTime && endTime && startTime < endTime
        } else {
            return startTime <= endTime
        }
    }

    React.useEffect(() => {
        if (open) {
            if (!creatingNewWorkHistory) {
                getServiceOrder(serviceOrderWorkHistory.service_order_id)
                setOverheadNote(serviceOrderWorkHistory.overhead_note)
                setDiagnosticMinutes(serviceOrderWorkHistory.diagnostic_minutes)
                setDiagnosticNote(serviceOrderWorkHistory.diagnostic_note ?? '')
                setStartTime(dayjs.utc(serviceOrderWorkHistory.start_time))
                setEndTime(dayjs.utc(serviceOrderWorkHistory.end_time))
            } else {
                if (selectedLocation.id !== 'all') {
                    setMySelectedLocation(selectedLocation)
                } else {
                    setMySelectedLocation({})
                }
                setStartTime(dayjs.utc())
                setEndTime(dayjs.utc())
            }
        } else {
            setServiceOrder({})
            setOverheadNote('')
            setDiagnosticMinutes(0)
            setDiagnosticNote('')
            setServiceData({})
            setSelectedTechnician({})
            setServiceOrderSearch('')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const enteredMinutes = Object.values(serviceData).reduce((accumulator, item) => accumulator + parseInt(item.recorded_minutes), 0) + diagnosticMinutes

    return (
        (<div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                maxWidth={'lg'}
                fullWidth
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <LoadingBackdropIndeterminate
                    open={saving}
                    text={"Saving"}
                />
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>{creatingNewWorkHistory ? 'Creating' : 'Editing'} Work History</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box>
                        {serviceOrderLoading &&
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: '9rem', // Adjust the height as needed
                                }}
                            >
                                <CircularProgress size="7rem" />
                            </Box>
                        }
                        {!serviceOrderLoading &&
                            <Grid container direction="column" spacing={2} sx={{ minWidth: '300px' }}>
                                {creatingNewWorkHistory &&
                                    <Grid size="grow" container alignItems={'center'}>
                                        <Grid size="auto">
                                            <TextField
                                                margin="dense"
                                                id="contractsearch"
                                                label="Search Service Order ID"
                                                placeholder="####"
                                                type="number"
                                                variant="filled"
                                                value={serviceOrderSearch}
                                                onChange={(e) => setServiceOrderSearch(e.target.value.trim())}
                                                onKeyDown={(e) => { if (e.key === 'Enter') { getServiceOrder(serviceOrderSearch) } }}
                                                autoComplete='off'
                                                onFocus={(e) => e.target.addEventListener('wheel', (e) => e.preventDefault())}
                                                sx={{
                                                    minWidth: '150px', maxWidth: '175px',
                                                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                                        display: "none",
                                                    },
                                                    "& input[type=number]": {
                                                        MozAppearance: "textfield",
                                                    },
                                                }}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                {<SearchIcon />}
                                                            </InputAdornment>
                                                        ),
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid size="auto">
                                            <Button variant="contained" onClick={() => getServiceOrder(serviceOrderSearch)} disabled={serviceOrderLoading} startIcon={<SearchIcon />}>Search</Button>
                                        </Grid>
                                    </Grid>
                                }
                                <Grid container spacing={2} size="grow" alignItems={'flex-start'}>
                                    <Grid size="grow" container alignItems={'center'} justifyContent={'flex-start'} spacing={1}>
                                        <Grid size="auto">
                                            <span><b>SO #{serviceOrder.id ?? '?????'}</b></span>
                                        </Grid>
                                        <Grid size="auto">
                                            {serviceOrder.id &&
                                                <Box>{serviceOrderChip(my_user, serviceOrder.status_name, serviceOrder.status_type)}</Box>
                                            }
                                        </Grid>
                                        {serviceOrder.customer &&
                                            <Grid size={12}>
                                                <span>Customer: {formatCustomerName(serviceOrder.customer)}</span>
                                            </Grid>
                                        }
                                        {serviceOrder.customer_item &&
                                            <Grid size={12}>
                                                <span>Item: {serviceOrder.customer_item.manufacturer} {serviceOrder.customer_item.model}</span>
                                            </Grid>
                                        }
                                    </Grid>
                                    {!creatingNewWorkHistory &&
                                        <Grid size="auto">
                                            <Grid container space={1} alignItems="center">
                                                <Grid sx={{ pr: 1 }} size="auto">
                                                    <LocationOnIcon />
                                                </Grid>
                                                <Grid size="auto">
                                                    <span>{findLocation(locations, serviceOrderWorkHistory.location_id)?.name}</span>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    }
                                    {creatingNewWorkHistory &&
                                        <Grid size="auto">
                                            <Autocomplete
                                                sx={{ color: 'rgba(0, 0, 0, 0.6)', width: '225px' }}
                                                // className={`header-location-selector-${theme.palette.brandcolor.contrastText === '#fff' ? 'light' : 'dark'}`}
                                                // color="brandcolor"
                                                getOptionLabel={(option) => `${option.name || ""}`}
                                                options={(my_user?.locations || [])}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                autoComplete
                                                includeInputInList
                                                disableClearable
                                                value={mySelectedLocation}
                                                noOptionsText="No Locations"
                                                onChange={(e, new_loc) => setMySelectedLocation(new_loc ?? {})}
                                                renderInput={(params) => {
                                                    return (<TextField {...params} label="Location" fullWidth autoComplete='off' />)
                                                }}
                                                renderOption={(props, option, { inputValue }) => {
                                                    const matches = match(props.key, inputValue, { insideWords: true, findAllOccurrences: true });
                                                    const parts = parse(props.key, matches);
                                                    const li_props = { ...props }
                                                    delete li_props.key

                                                    return (
                                                        <li key={props.id} {...li_props} >
                                                            <div>
                                                                {parts.map((part, index) => (
                                                                    <span
                                                                        key={index}
                                                                        style={{
                                                                            fontWeight: part.highlight ? 700 : 400,
                                                                        }}
                                                                    >
                                                                        {part.text}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </li>
                                                    );
                                                }}
                                            />
                                        </Grid>
                                    }
                                    {known_user &&
                                        <Grid size="auto">
                                            <Grid container space={1} alignItems="center">
                                                <Grid sx={{ pr: 1 }} size="auto">
                                                    <span><UserAvatar user={known_user} /></span>
                                                </Grid>
                                                <Grid size="auto">
                                                    <span>{known_user?.name}</span>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    }
                                    {!known_user &&
                                        <Grid size="auto">
                                            <Box display="flex" justifyContent="flex-end">
                                                <Autocomplete
                                                    sx={{ color: 'rgba(0, 0, 0, 0.6)', width: '275px' }}
                                                    getOptionLabel={(option) => `${option.name || ""}`}
                                                    options={build_users_list(users, my_user, false, false, true, selectedLocation?.id ?? 'all')}
                                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                                    autoComplete
                                                    fullWidth
                                                    includeInputInList
                                                    // disableClearable
                                                    value={selectedTechnician}
                                                    noOptionsText="No Users"
                                                    loading={isLoadingUsers}
                                                    onChange={(event, newValue) => {
                                                        setSelectedTechnician(newValue ?? {})
                                                    }}
                                                    slotProps={{
                                                        popper: {
                                                            sx: {
                                                                minWidth: '250px'
                                                            }
                                                        }
                                                    }}
                                                    renderInput={(params) => {
                                                        let avatar = undefined
                                                        if (params.inputProps["aria-expanded"]) {
                                                            avatar = (
                                                                <span><UserAvatar user={{}} /></span>
                                                            )
                                                        } else {
                                                            avatar = (
                                                                <span><UserAvatar user={findUser(users, selectedTechnician?.id) ?? selectedTechnician} /></span>
                                                            )
                                                        }
                                                        return (
                                                            (<div style={{ position: "relative" }}>
                                                                <span
                                                                    style={{
                                                                        position: "absolute",
                                                                        transform: "translateY(50%)",
                                                                        marginLeft: "5px"
                                                                    }}
                                                                >
                                                                    {avatar}
                                                                </span>
                                                                <TextField
                                                                    {...params}
                                                                    label="Select Technician"
                                                                    required
                                                                    fullWidth
                                                                    autoComplete='off'
                                                                    type="text"
                                                                    slotProps={{
                                                                        htmlInput: {
                                                                            ...params.inputProps,
                                                                            style: {
                                                                                padding: "4px 4px 0 32px",
                                                                                height: '36px'
                                                                            }
                                                                        },

                                                                        inputLabel: {
                                                                            shrink: true,
                                                                            ...params.InputLabelProps,
                                                                        }
                                                                    }} />
                                                            </div>)
                                                        );
                                                    }}
                                                    renderOption={(props, option, { inputValue }) => {
                                                        const matches = match(props.key, inputValue, { insideWords: true, findAllOccurrences: true });
                                                        const parts = parse(props.key, matches);
                                                        const li_props = { ...props }
                                                        delete li_props.key

                                                        return (
                                                            (<li key={props.id} {...li_props}>
                                                                <Grid container space={1} alignItems="center">
                                                                    <Grid sx={{ pr: 1 }} size="auto">
                                                                        <span><UserAvatar user={option} /></span>
                                                                    </Grid>
                                                                    <Grid size="auto">
                                                                        {parts.map((part, index) => (
                                                                            <span
                                                                                key={index}
                                                                                style={{
                                                                                    fontWeight: part.highlight ? 700 : 400,
                                                                                }}
                                                                            >
                                                                                {part.text}
                                                                            </span>
                                                                        ))}
                                                                    </Grid>
                                                                </Grid>
                                                            </li>)
                                                        );
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    }
                                </Grid>
                                {serviceOrder.id &&
                                    <Grid container spacing={2} size="grow">
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <Grid size="auto">
                                                <DateTimePicker
                                                    value={startTime.local()}
                                                    label="Start Time"
                                                    onChange={(newValue) => {
                                                        setStartTime(newValue)
                                                    }}
                                                    onAccept={(newValue) => {
                                                        if (newValue > endTime) {
                                                            setEndTime(newValue)
                                                        }
                                                    }}
                                                    timeSteps={{ hours: 1, minutes: 1 }}
                                                    sx={{ width: '250px' }}
                                                />
                                            </Grid>
                                            <Grid size="auto">
                                                <DateTimePicker
                                                    value={endTime.local()}
                                                    label="End Time"
                                                    onChange={(newValue) => {
                                                        setEndTime(newValue)
                                                    }}
                                                    minDateTime={creatingNewWorkHistory ? startTime.add(1, 'second') : startTime}
                                                    timeSteps={{ hours: 1, minutes: 1 }}
                                                    sx={{ width: '250px' }}
                                                />
                                            </Grid>
                                        </LocalizationProvider>
                                    </Grid>
                                }
                                {serviceOrder.id &&
                                    <Grid container spacing={2} size="grow">
                                        <Grid size={4}>
                                            <span>Service</span>
                                        </Grid>
                                        <Grid size={2}>
                                            <span>Minutes</span>
                                        </Grid>
                                        <Grid size={6}>
                                            <span>Comments (optional)</span>
                                        </Grid>
                                        <Grid container size={12} spacing={2} alignItems={"center"}>
                                            <Grid size={4}>
                                                <span>Diagnostic</span>
                                            </Grid>
                                            <Grid size={2}>
                                                <TextField
                                                    margin="dense"
                                                    id="diagnostic_minutes"
                                                    hiddenLabel
                                                    size="small"
                                                    type="number"
                                                    fullWidth
                                                    variant="filled"
                                                    value={parseInt(diagnosticMinutes).toString()}
                                                    onChange={(e) => setDiagnosticMinutes(Math.max(parseInt(e.target.value || 0), 0))}
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <TextField
                                                    margin="dense"
                                                    id="diagnostic_note"
                                                    hiddenLabel
                                                    size="small"
                                                    type="text"
                                                    fullWidth
                                                    variant="filled"
                                                    value={diagnosticNote}
                                                    onChange={(e => setDiagnosticNote(e.target.value))}
                                                />
                                            </Grid>
                                        </Grid>
                                        {(serviceOrder?.service_order_services ?? []).map((service) => (
                                            <Grid container key={service.id} size={12} spacing={2} alignItems={"center"}>
                                                <Grid container size={4} alignItems={"center"}>
                                                    <Grid size="auto">
                                                        <Chip label={SERVICE_ORDER_SERVICE_FRIENDLY_NAMES[service.status]} color={SERVICE_ORDER_SERVICE_COLOR_MAP[service.status] || 'primary'} />
                                                    </Grid>
                                                    <Grid size="grow">
                                                        <span>{service.name}</span>
                                                    </Grid>
                                                </Grid>
                                                <Grid size={2}>
                                                    <TextField
                                                        margin="dense"
                                                        id="recorded_minutes"
                                                        hiddenLabel
                                                        size="small"
                                                        type="number"
                                                        fullWidth
                                                        variant="filled"
                                                        value={parseInt(serviceData[service.id]?.recorded_minutes).toString()}
                                                        onChange={handleChange(service.id, "recorded_minutes")}
                                                    />
                                                </Grid>
                                                <Grid size={6}>
                                                    <TextField
                                                        margin="dense"
                                                        id="note"
                                                        hiddenLabel
                                                        size="small"
                                                        type="text"
                                                        fullWidth
                                                        variant="filled"
                                                        value={serviceData[service.id]?.note}
                                                        onChange={handleChange(service.id, "note")}
                                                    />
                                                </Grid>
                                            </Grid>
                                        ))}
                                        <Grid size={12}>
                                            <hr />
                                        </Grid>

                                        <Grid container spacing={2} alignItems={"center"} size={12}>
                                            {enteredMinutes > totalMinutes &&
                                                <Grid size={12}>
                                                    <Alert severity="warning">Work History record is {totalMinutes}m long. You've entered {enteredMinutes} minutes of service time</Alert>
                                                </Grid>
                                            }
                                            <Grid size={4}>
                                                <span><u>Overhead</u></span>
                                            </Grid>
                                            <Grid size={2}>
                                                <TextField
                                                    margin="dense"
                                                    id="recorded_minutes"
                                                    hiddenLabel
                                                    size="small"
                                                    type="number"
                                                    fullWidth
                                                    variant="outlined"
                                                    value={Math.max(0, totalMinutes - enteredMinutes)}
                                                    slotProps={{
                                                        input: {
                                                            readOnly: true,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <TextField
                                                    margin="dense"
                                                    id="note"
                                                    hiddenLabel
                                                    size="small"
                                                    type="text"
                                                    fullWidth
                                                    variant="filled"
                                                    value={overheadNote}
                                                    onChange={(event) => setOverheadNote(event.target.value)}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                }
                                {/* <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="addservicenote"
                                        label="Notes (optional)"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        minRows={2}
                                        value={notes}
                                        onChange={(event) => setNotes(event.target.value)}
                                        sx={{ mt: 0 }}
                                        slotProps={{
                                            input: { maxLength: 2055 }
                                        }}
                                    />
                                </Grid> */}
                            </Grid>
                        }
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                        <Grid size="auto">
                        </Grid>
                        <Grid size="auto">
                            <Button variant="contained" autoFocus onClick={creatingNewWorkHistory ? create : save} disabled={serviceOrderLoading || saving || !ready()}>
                                {creatingNewWorkHistory ? 'Create' : 'Save'}
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog >
        </div >)
    );
}
